'use client';

import Icon from '@/components/ui/AppIcon';

interface ForceMajeureSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export default function ForceMajeureSection({ isExpanded, onToggle }: ForceMajeureSectionProps) {
  const forceMajeureEvents = [
    'Natural disasters (earthquakes, floods, cyclones)',
    'Pandemics and public health emergencies',
    'Acts of terrorism or civil unrest',
    'Government orders or regulatory restrictions',
    'Utility failures beyond our control',
    'Strikes or labor disputes',
    'War or military operations'
  ];

  const procedures = [
    {
      title: 'Service Disruptions',
      description: 'In the event of force majeure, Proximus will make reasonable efforts to minimize service disruptions.',
      actions: [
        'Immediate notification to all affected guests via email and SMS',
        'Assessment of situation and estimated resolution timeline',
        'Alternative accommodation arrangements if property uninhabitable',
        'Prorated refunds for services not rendered during disruption'
      ]
    },
    {
      title: 'Emergency Procedures',
      description: 'Safety protocols and emergency response procedures during unforeseen circumstances.',
      actions: [
        '24/7 emergency hotline activated for guest assistance',
        'Evacuation procedures communicated clearly to all guests',
        'Coordination with local authorities and emergency services',
        'Regular updates provided until situation resolved'
      ]
    },
    {
      title: 'Accommodation Alternatives',
      description: 'Options available to guests when force majeure events impact their stay.',
      actions: [
        'Relocation to partner properties at no additional cost',
        'Full refund of unused portion of booking',
        'Rebooking at future date with rate protection',
        'Travel assistance and coordination support'
      ]
    }
  ];

  return (
    <section id="force-majeure" className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Icon name="ExclamationCircleIcon" size={20} className="text-primary" />
              <span className="text-primary text-sm font-medium">Section 5</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-text-primary mb-4">
              Force Majeure
            </h2>
            <p className="text-lg text-text-secondary">
              Clauses addressing service disruptions, emergency procedures, and accommodation alternatives during unforeseen circumstances.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-premium border border-border overflow-hidden">
            <button
              onClick={onToggle}
              className="w-full px-6 py-6 flex items-center justify-between hover:bg-muted transition-colors duration-300"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="ShieldExclamationIcon" size={24} className="text-primary" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-semibold text-text-primary">View Force Majeure Clauses</h3>
                  <p className="text-sm text-text-secondary mt-1">Click to expand emergency procedures and policies</p>
                </div>
              </div>
              <Icon 
                name={isExpanded ? "ChevronUpIcon" : "ChevronDownIcon"} 
                size={24} 
                className="text-text-secondary flex-shrink-0" 
              />
            </button>

            {isExpanded && (
              <div className="px-6 pb-6 space-y-6 animate-scale-in">
                <div className="border-t border-border pt-6">
                  <h4 className="text-lg font-semibold text-text-primary mb-4">Qualifying Force Majeure Events</h4>
                  <p className="text-text-secondary mb-4">
                    Force majeure refers to unforeseeable circumstances that prevent Proximus from fulfilling contractual obligations. These include but are not limited to:
                  </p>
                  <ul className="space-y-2">
                    {forceMajeureEvents.map((event, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <Icon name="ExclamationTriangleIcon" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-text-secondary">{event}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {procedures.map((procedure, index) => (
                  <div key={index} className="border-t border-border pt-6">
                    <h4 className="text-lg font-semibold text-text-primary mb-2">{procedure.title}</h4>
                    <p className="text-text-secondary mb-4">{procedure.description}</p>
                    <ul className="space-y-2">
                      {procedure.actions.map((action, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Icon name="CheckCircleIcon" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                          <span className="text-text-secondary">{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                <div className="border-t border-border pt-6">
                  <div className="bg-accent/5 border border-accent/20 rounded-xl p-6">
                    <div className="flex items-start space-x-3">
                      <Icon name="InformationCircleIcon" size={24} className="text-accent flex-shrink-0" />
                      <div>
                        <h5 className="font-semibold text-text-primary mb-2">24/7 Emergency Contact</h5>
                        <p className="text-sm text-text-secondary mb-2">
                          In case of emergency or force majeure event, contact our emergency hotline immediately:
                        </p>
                        <p className="text-sm font-semibold text-text-primary">+91 98765 43210</p>
                        <p className="text-sm text-text-secondary mt-2">Email: emergency@proximusapartments.com</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}